import {Component, OnInit} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {AbstractMerchantSupplierComponent} from 'app/core/common/abstract.merchant-supplier.component';
import {AcceptanceAccount} from 'app/core/models/acceptanceaccount';
import {Merchant} from 'app/core/models/merchant';
import {PointOfService} from 'app/core/models/pointOfService';
import {AcceptanceAccountsService} from 'app/core/services/acceptanceAccounts.service';
import {MerchantService} from 'app/core/services/merchant.service';
import {NotifMessageService} from 'app/core/services/notifmessage.service';
import {PaginationService} from 'app/core/services/pagination.service';
import {PosService} from 'app/core/services/pos.service';
import {SupplierService} from 'app/core/services/supplier.service';
import {NGXLogger} from 'ngx-logger';
import {combineLatest, forkJoin, Observable, of, ReplaySubject, Subscription} from 'rxjs';
import {map, switchMap, take, tap} from 'rxjs/operators';
import {AuthenticatedRouteGuard} from 'security/authenticatedRouteGuard.service';
import {KeycloakService} from 'security/keycloak.service';
import {tableEventChange} from '../table-utils/classes/tableChangeEvent';
import {actionType} from '../table-utils/enums/actionType';
import {columnType} from '../table-utils/enums/columnType';

@Component({
    selector: 'app-acceptance-accounts-page',
    templateUrl: './acceptance-accounts-page.component.html'
})
export class AcceptanceAccountsPageComponent extends AbstractMerchantSupplierComponent implements OnInit {

    dataSubject$: ReplaySubject<any[]> = new ReplaySubject<any[]>();
    totalLength$: ReplaySubject<number> = new ReplaySubject<number>();

    isLoadingResults: boolean = true;
    isDeletedIncluded: boolean = false;

    statusItems: Array<String> = new Array<String>();

    columns: {
        data: {
            title: string;
            type: columnType
        }[],
        clickable: boolean;
    };

    pager: {
        page: number,
        sort: string,
        size: number
    } = {
        page: 0,
        sort: "createdDate,DESC",
        size: 20
    };

    criteria: AcceptanceAccount = new AcceptanceAccount();

    columnPropertyMapping = {
        "ACCEPTANCE_ACCOUNT_ID": "acceptanceAccountId",
        "ACCEPTANCE_ACCOUNT_PROVIDER": "acceptanceAccountProvider",
        "MERCHANT": "merchantId",
        "POS": "posId",
        "SUPPLIER": "supplierId",
        "CREATION_DATE": "createdDate",
        "ACTIONS": "actions"
    };

    searchForm: FormGroup = new FormGroup({
        merchantId: new FormControl(),
        posId: new FormControl()
    });

    modalResponseSubscription: Subscription;

    isSearchByMerchant: boolean = true;
    acceptanceAccount: Array<AcceptanceAccount>;

    merchants: Array<Merchant>;
    posList: Array<PointOfService>;

    constructor(
        protected log: NGXLogger,
        protected paginationService: PaginationService,
        protected notifService: NotifMessageService,
        protected acceptanceAccountService: AcceptanceAccountsService,
        public router: Router,
        protected roleGuard: AuthenticatedRouteGuard,
        protected keycloakService: KeycloakService,
        protected merchantService: MerchantService,
        protected posService: PosService,
        protected translate: TranslateService,
        protected supplierService: SupplierService) {
        super(supplierService, merchantService, posService, log, router);
    }

    ngOnInit() {
        this.columns = {
            data: [
                {title: "ACCEPTANCE_ACCOUNT_ID", type: columnType.text},
                {title: "ACCEPTANCE_ACCOUNT_PROVIDER", type: columnType.text},
                {title: "MERCHANT", type: columnType.text},
                {title: "POS", type: columnType.translateText},
                {title: "SUPPLIER", type: columnType.text},
                {title: "CREATION_DATE", type: columnType.date},
                {title: "ACTIONS", type: columnType.actions}
            ],
            clickable: true
        };
        this.merchantService.getAllMerchants().subscribe(merchants => {
            this.merchants = merchants.content;
        });
        this.posService.getPosTotalElement().subscribe(pos => {
            let pager = {page: 0, size: pos.totalElements, sort: "id,ASC"};
            this.posService.findByCriteria({}, pager, false).subscribe(posList => {
                let posEnroll: PointOfService = new PointOfService();
                posEnroll.id = -1;
                posEnroll.name = "ENROLLEMENT";
                this.posList = posList.content;
                this.posList.push(posEnroll);
            });
        });
        this.keycloakService.rolesObservable.subscribe(roles => {
            if (roles.length !== 0) {
                if (this.roleGuard.isEnabled('ACC_ACCOUNT_READ')) {
                    this.loadData();
                }
            }
        });
    }

    ngOnDestroy() {
        super.closeSubscribtions();
    }

    formatAcceptanceAccount(acceptanceAccount: AcceptanceAccount, supplierName: string, merchantName: string, posName: string) {
        const actions = [];

        if (this.roleGuard.isEnabled('ACC_ACCOUNT_EDIT') && !acceptanceAccount.deleted) {
            actions.push({
                "name": "EDIT",
                "type": actionType.navigation,
                "value": `/service/acceptanceaccounts/edit/${acceptanceAccount.id}`
            });
        }
        if (this.roleGuard.isEnabled('ACC_ACCOUNT_READ')) {
            actions.push({
                "name": "DETAILS",
                "type": actionType.navigation,
                "value": `/service/acceptanceaccounts/${acceptanceAccount.id}`
            });
        }
        if (this.roleGuard.isEnabled('ACC_ACCOUNT_DELETE') && !acceptanceAccount.deleted) {
            actions.push({
                "name": "DELETE",
                "type": actionType.eventEmitter,
                "value": acceptanceAccount.id
            })
        }

        return {
            id: acceptanceAccount.id,
            ACCEPTANCE_ACCOUNT_ID: acceptanceAccount.acceptanceAccountId,
            ACCEPTANCE_ACCOUNT_PROVIDER: acceptanceAccount.acceptanceAccountProvider,
            MERCHANT: `${merchantName} (${acceptanceAccount.merchantId})`,
            POS: `${posName} (${acceptanceAccount.posId})`,
            SUPPLIER: `${supplierName} (${acceptanceAccount.supplierId})`,
            deleted: acceptanceAccount.deleted,
            CREATION_DATE: acceptanceAccount.createdDate,
            ACTIONS: actions
        }
    }

    loadData(isLoadingSearch: boolean = false): void {
        this.isLoadingResults = true;
        this.acceptanceAccountService
            .findByCriteria(this.criteria, this.pager, this.isDeletedIncluded)
            .pipe(
                tap(acceptanceAccounts => this.totalLength$.next(acceptanceAccounts.totalElements)),
                switchMap(acceptanceAccounts => {
                    if (acceptanceAccounts.content.length !== 0) {
                        const acceptanceAccounts$ = acceptanceAccounts.content.map(acceptanceAccount => {
                            const supplierName$ = this.getSupplierName(acceptanceAccount.supplierId);
                            const merchantName$ = this.getMerchantName(acceptanceAccount.merchantId);
                            const posName$ = this.getPosName(acceptanceAccount.posId);
                            return forkJoin(([supplierName$, merchantName$, posName$])).pipe(
                                map(([supplierName, merchantName, posName]) => {
                                        return this.formatAcceptanceAccount(acceptanceAccount, supplierName, merchantName, posName)
                                    }
                                ));
                        });
                        return combineLatest(acceptanceAccounts$)
                    }
                    if (isLoadingSearch) {
                        this.notifService.notifyInfo('EMPTY_RESULT');
                    }
                    return of([]);
                }),
                map(acceptanceAccounts => {
                    this.isLoadingResults = false;
                    if (acceptanceAccounts === null) {
                        return [];
                    }
                    return acceptanceAccounts;
                }),
            ).subscribe(acceptanceAccounts => {
            this.dataSubject$.next(acceptanceAccounts);
        })
    }

    deleteButton(id) {
        this.notifService.emitModalWithDetails("DELETE_ACC_ACCOUNT", ["ACC_ACCOUNT_DELETION", "DELETE_ACC_ACCOUNT"]);
        this.modalResponseSubscription = this.notifService.modalResponseSubject$.subscribe(resp => {
            if (resp) {
                this.acceptanceAccountService.deleteAcceptanceAccount(id).subscribe(
                    () => {
                        this.loadData();
                        this.notifService.notifySuccess('ACC_ACCOUNT_DELETED');
                        this.modalResponseSubscription.unsubscribe();
                    }
                );
            } else {
                this.modalResponseSubscription.unsubscribe();
            }
        });
    }

    getPOSAndCache(id: number): Observable<PointOfService> {
        if (this.posObservables.has(id)) {
            return this.posObservables.get(id);
        } else {
            let obs = this.posService.getPosById(id).pipe(take(1));
            this.posObservables.set(id, obs);
            return obs;

        }
    }

    getPosName(id: number): Observable<string> {
        if (id) {
            if (this.posNameObservables.has(id)) {
                return this.posNameObservables.get(id);
            } else {
                let res = this.getPOSAndCache(id).pipe(map(s => s.name));
                this.posNameObservables.set(id, res);
                return res;
            }
        } else {
            return this.nameNotFoundObs;
        }
    }

    tableChange(event: tableEventChange) {
        const sortProperty = this.columnPropertyMapping[event.sortEvent.active];
        const sortDirection = event.sortEvent.direction.toUpperCase();
        this.pager = {
            page: event.pageIndex,
            sort: sortDirection !== "" && sortProperty ? [sortProperty, sortDirection].join(",") : "lastModifiedDate,DESC",
            size: event.pageSize
        }
        this.loadData();
    }

    search(): void {
        this.criteria = new AcceptanceAccount();
        for (let field in this.searchForm.value) {
            if (!this.searchForm.value[field]) {
                delete this.searchForm.value[field];
            } else {
                this.criteria[field] = this.searchForm.value[field]
            }
        }
        this.loadData(true);
    }

    clearSearch(): void {
        this.searchForm.reset();
        this.criteria = new AcceptanceAccount();
        this.loadData();
    }

    includeDeleted(event: MatCheckboxChange): void {
        this.isDeletedIncluded = event.checked;
        this.loadData();
    }

    details(id: number): void {
        this.router.navigate([`/service/acceptanceaccounts/${id}`]);
    }

}
